import * as THREE from "three";
import { isModuleToModuleConnection, traverseOwnObjects } from "../../utils/geometry.js";
import { normalizeWoodType } from "../../utils/constants.js";
import {
  ENABLE_ANISOTROPY_MAPS,
  configureAnisotropyMap,
} from "../../materials/AnisotropyMap.js";

// Both spellings mean "group's dark fabric": bonbon3-t/a/f/c use Base_mesh, bonbon3-d DarkFabric_mesh.
const DARK_FABRIC_NODE_PREFIXES = ["Base_mesh", "DarkFabric_mesh"];

export class FurnitureMaterials {
  // Get material zones for a specific furniture item
  getMaterialZones(furnitureData) {
    if (!furnitureData) return ["fabric"]; // Default fallback
    // Check if furniture has properties.materialZones
    if (furnitureData.properties?.materialZones) {
      return furnitureData.properties.materialZones;
    }
    return ["fabric"]; // Ultimate fallback
  }

  async applyMaterialToFurniture(furnitureElement, fabricId, propagateToChildren = false, resolution = "512") {
    const materialManager = this.app.getMaterialManager();
    if (!materialManager || !furnitureElement || !furnitureElement.mesh) {
      console.warn(
        "Cannot apply material: missing MaterialManager or furniture mesh",
      );
      return false;
    }

    try {
      const [fabricData, darkFabricData] = await this._loadFabricWithDark(materialManager, fabricId, resolution);
      if (!fabricData) {
        console.warn(`Fabric not found: ${fabricId}`);
        return false;
      }

      // Get the material zones for this furniture
      const materialZones = this.getMaterialZones(
        furnitureElement.furnitureData || furnitureElement,
      );

      // Apply material only to allowed zones in the furniture; components change only through propagation.
      traverseOwnObjects(furnitureElement, (child) => {
        if (
          child.isMesh &&
          !child.userData?.isOutline &&
          this.isFabricMaterial(child.material, materialZones)
        ) {
          // Determine which zone this mesh belongs to based on its node name
          const materialZone = this.determineMaterialZone(child);

          const fabricForMesh =
            darkFabricData && DARK_FABRIC_NODE_PREFIXES.some((p) => child.name?.startsWith(p))
              ? darkFabricData
              : fabricData;

          const newMaterial = materialManager.createMaterialWithFabric(
            fabricForMesh,
            // createMaterialWithFabric upgrades to physical itself when nap is on.
            "MeshStandardMaterial",
            materialZone,
          );

          if (!newMaterial) {
            console.warn("Failed to create material for", child.name);
            return; // Skip this mesh if material creation failed
          }

          // Copy existing properties
          if (child.material) {
            newMaterial.transparent = child.material.transparent || false;
            newMaterial.opacity = child.material.opacity || 1.0;
          }

          // Dispose old material
          if (child.material && child.material.dispose) {
            child.material.dispose();
          }

          child.material = newMaterial;
          child.material.needsUpdate = true;
        }
      });

      // Store applied fabric info
      furnitureElement.appliedFabric = fabricId;
      furnitureElement.fabricId = fabricId;
      furnitureElement.fabricCode = fabricData.fabricCode;
      // Propagate to children if requested (only during live editing of base modules)
      if (propagateToChildren && furnitureElement.attachedChildren) {
        for (const child of furnitureElement.attachedChildren) {
          // Only propagate to non-module attachments (panels, backrests, cushions)
          if (!isModuleToModuleConnection(child, furnitureElement)) {
            // A shared accessory wears fewer fabric groups than its host module, and the
            // backend prices an unsold combination at 0 — resolve rather than inherit blindly.
            const childFabricId = materialManager.resolveFabricForProduct(
              fabricId, child.furnitureData?.urlCode);
            if (!childFabricId) continue;
            await this.applyMaterialToFurniture(child, childFabricId, true, resolution);
          }
        }
      }

      // Shown at 512; quietly upgrade this fabric to 1k in the background (deduped, on idle).
      if (resolution === "512") {
        this.scheduleHiResUpgrade(fabricId);
      }

      // Trigger debounced save after applying material
      this.debouncedSaveState();
      // Direct rather than waiting out debouncedSave's 1s timer — a fabric change
      // is a visible edit, and the material instances were swapped so the tracer
      // needs to re-read the scene.
      this.app.getPathTracingManager?.()?.invalidate();

      return true;
    } catch (error) {
      console.error("Error applying material:", error);
      return false;
    }
  }

  // Background 1k upgrade for a 512 fabric, deduped and coalesced; held until the loader goes quiet + a grace delay so it never rides in right behind the just-clicked 512.
  scheduleHiResUpgrade(fabricId) {
    if (!fabricId) return;
    if (!this._pendingHiResUpgrades) this._pendingHiResUpgrades = new Set();
    this._pendingHiResUpgrades.add(fabricId);
    if (this._hiResUpgradeScheduled) return;
    this._hiResUpgradeScheduled = true;

    const HI_RES_GRACE_MS = 1500;
    const materialManager = this.app.getMaterialManager();
    const tick = () => {
      // A 512 is still loading (initial paint or a fresh click) — check back shortly.
      if (materialManager?.hasPendingLoads?.()) {
        setTimeout(tick, 500);
        return;
      }
      this._hiResUpgradeScheduled = false;
      const ids = [...this._pendingHiResUpgrades];
      this._pendingHiResUpgrades.clear();
      ids.forEach((id) => this.upgradeFabricToHighRes(id));
    };
    setTimeout(tick, HI_RES_GRACE_MS);
  }

  /**
   * A fabric's data plus its group's darker fabric for Base nodes, when materials.json declares
   * one (settings.darkFabricByGroup) and it isn't the fabric itself.
   * @returns {Promise<[Object|null, Object|null]>}
   */
  _loadFabricWithDark(materialManager, fabricId, resolution) {
    const fabricEntry = materialManager.fabrics?.[fabricId];
    const darkFabricId = fabricEntry ? materialManager.getDarkFabricIdForGroup(fabricEntry.group) : null;
    return Promise.all([
      materialManager.getFabricData(fabricId, resolution),
      darkFabricId && darkFabricId !== fabricId
        ? materialManager.getFabricData(darkFabricId, resolution)
        : Promise.resolve(null),
    ]);
  }

  // Fetch a fabric's 1k maps and swap them onto live materials in place (preserving tiling/offset), matched by `${fabricId}_fabric` name so Base_mesh (dark same-group) upgrades alongside the primary fabric.
  async upgradeFabricToHighRes(fabricId) {
    const materialManager = this.app.getMaterialManager();
    if (!materialManager) return;

    const [hi, hiDark] = await this._loadFabricWithDark(materialManager, fabricId, "1k");
    if (!hi) return;

    const hiByName = { [`${hi.id}_fabric`]: hi };
    if (hiDark) hiByName[`${hiDark.id}_fabric`] = hiDark;

    for (const element of this.getAllFurniture()) {
      if (element.appliedFabric !== fabricId || !element.mesh) continue;
      element.mesh.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        const src = hiByName[child.material.name];
        if (!src) return;
        const mat = child.material;
        if (mat.map) {
          const newMap = materialManager.cloneMapMatching(src.texture, mat.map, { srgb: true });
          mat.map.dispose();
          mat.map = newMap;
        }
        if (mat.normalMap && src.normalMap) {
          const newNormal = materialManager.cloneMapMatching(src.normalMap, mat.normalMap);
          mat.normalMap.dispose();
          mat.normalMap = newNormal;
        }
        // 1k nap map upgrade; no-ops while ENABLE_ANISOTROPY_MAPS is false.
        if (ENABLE_ANISOTROPY_MAPS && src.anisotropyMap && mat.anisotropyMap) {
          const newAniso = materialManager.cloneMapMatching(src.anisotropyMap, mat.anisotropyMap);
          configureAnisotropyMap(newAniso);
          mat.anisotropyMap.dispose();
          mat.anisotropyMap = newAniso;
        }
        mat.needsUpdate = true;
      });
    }

    // A live trace holds the 512 maps (and their graded copies) — re-derive from the 1k set.
    this.app.getPathTracingManager()?.invalidate();
  }

  isFabricMaterial(material, allowedZones = ["fabric"]) {
    if (!material) return false;

    const materialName = material.name ? material.name.toLowerCase() : "";

    // CRITICAL: Never apply fabric to materials that already have specific textures
    // These materials have their own dedicated textures loaded in setupGLTFMaterial
    if (
      materialName.includes("_wood") ||
      materialName.includes("_label") ||
      materialName.includes("_metal") ||
      materialName.includes("_plastic") ||
      materialName.includes("_base_inner")
    ) {
      return false;
    }

    const cacheKey = `isFabricMaterial_${allowedZones.join("_")}`;
    if (material.userData?.[cacheKey] !== undefined) {
      return material.userData[cacheKey];
    }

    const isFabric = this.matchesMaterialZone(materialName, allowedZones);

    // Cache the result
    if (!material.userData) material.userData = {};
    material.userData[cacheKey] = isFabric;

    return isFabric;
  }

  matchesMaterialZone(materialName, allowedZones) {
    // Define zone matchers
    const zoneMatchers = {
      fabric: () => this.isFabricZone(materialName),
      legs: () => this.isLegsZone(materialName),
      metal: () => this.isMetalZone(materialName),
      plastic: () => this.isPlasticZone(materialName),
      accents: () => this.isAccentsZone(materialName),
    };

    // Check each allowed zone
    for (const zone of allowedZones) {
      const matcher = zoneMatchers[zone];
      if (matcher && matcher()) {
        return true;
      }
    }

    return false;
  }

  isFabricZone(materialName) {
    const fabricPatterns = [
      "_fabric",
      "fabric",
      "upholstery",
      "seat",
      "cushion",
    ];
    return materialName === "fabric" ||
           fabricPatterns.some((pattern) => materialName.includes(pattern));
  }

  isLegsZone(materialName) {
    const legsPatterns = ["_wood", "leg", "frame", "wood"];
    return materialName === "legs" ||
           legsPatterns.some((pattern) => materialName.includes(pattern));
  }

  isMetalZone(materialName) {
    const metalPatterns = ["_metal", "metal", "hardware"];
    return metalPatterns.some((pattern) => materialName.includes(pattern));
  }

  isPlasticZone(materialName) {
    const plasticPatterns = ["_plastic", "plastic"];
    return plasticPatterns.some((pattern) => materialName.includes(pattern));
  }

  isAccentsZone(materialName) {
    const accentsPatterns = ["accent", "label", "button"];
    return accentsPatterns.some((pattern) => materialName.includes(pattern));
  }

  determineMaterialZone(mesh) {
    if (!mesh) return "fabric";

    // Use node name (mesh name) for zone detection - much simpler and more reliable
    const nodeName = mesh.name ? mesh.name.toLowerCase() : "";

    // Remove Blender auto-numbering suffixes like .001, .002, etc.
    const cleanName = nodeName.replace(/\.\d{3}$/, "");

    // Direct node name matching
    if (cleanName.includes("fabric")) return "fabric";
    if (cleanName.includes("leg")) return "legs";
    if (
      cleanName.includes("metal") ||
      cleanName.includes("wheel") ||
      cleanName.includes("hinge") ||
      cleanName.includes("hindge") ||
      cleanName.includes("screw")
    )
      return "metal";
    if (cleanName.includes("plastic") || cleanName.includes("rubber"))
      return "plastic";
    if (
      cleanName.includes("label") ||
      cleanName.includes("button") ||
      cleanName.includes("accent")
    )
      return "accents";
    return "fabric"; // Default fallback
  }

  setupGLTFMaterial(originalMaterial, meshName, mesh = null) {
    // Create a new material based on the original GLTF material
    const materialName = originalMaterial.name || "";
    const meshNameLower = meshName ? meshName.toLowerCase() : "";
    const materialNameLower = materialName.toLowerCase();

    // base_fabric = inside of choice modules; fixed black, never receives user fabric
    if (meshNameLower.includes("base_fabric")) {
      return new THREE.MeshStandardMaterial({
        color: 0x000000,
        roughness: 0.9,
        metalness: 0.0,
        name: materialName + "_base_inner",
      });
    }

    // Prefer the material name when it carries a recognizable zone keyword
    // (e.g. "Black plastic", "Legs.002"). Only fall back to the mesh name when
    // the material name is generic (e.g. "Material.001", "Plastika_Drzaci_MTL")
    // so multi-primitive meshes like `Mesh.047` don't force every primitive
    // into a single zone.
    const MATERIAL_ZONE_KEYWORDS = [
      "fabric", "upholstery", "seat", "cushion",
      "wood", "leg", "frame",
      "metal", "steel", "aluminum", "chrome", "hinge", "hindge", "screw",
      "plastic", "rubber",
      "label", "logo", "brand", "accent", "button",
    ];
    const materialNameHasZoneInfo = MATERIAL_ZONE_KEYWORDS.some((k) =>
      materialNameLower.includes(k)
    );

    let effectiveMaterialName = materialName;
    if (meshName && !materialNameHasZoneInfo) {
      if (
        meshNameLower.includes("metal") ||
        meshNameLower.includes("screw") ||
        meshNameLower.includes("wheel") ||
        meshNameLower.includes("hinge") ||
        meshNameLower.includes("hindge")
      ) {
        effectiveMaterialName = "Metal";
      } else if (
        meshNameLower.includes("seat") ||
        meshNameLower.includes("fabric") ||
        meshNameLower.includes("cushion")
      ) {
        effectiveMaterialName = "Fabric";
      } else if (
        // Before leg/wood: a plastic leg is plastic, not a wood-textured one.
        meshNameLower.includes("plastic") ||
        meshNameLower.includes("rubber")
      ) {
        effectiveMaterialName = "Plastic";
      } else if (
        meshNameLower.includes("leg") ||
        meshNameLower.includes("wood")
      ) {
        effectiveMaterialName = "Legs";
      } else if (
        meshNameLower.includes("label") ||
        meshNameLower.includes("logo") ||
        meshNameLower.includes("accent") ||
        meshNameLower.includes("button")
      ) {
        effectiveMaterialName = "Label";
      } else {
        effectiveMaterialName = "Fabric"; // Default to fabric for unknown
      }
    }

    // Determine material type based on name and mesh context
    const materialType = this.determineGLTFMaterialType(
      effectiveMaterialName,
      meshName,
    );

    // Create appropriate material for the type
    switch (materialType) {
      case "fabric": {
        let roughness = 0.8;
        const fabricZone = this.app.getMaterialManager()?.materialZoneProperties?.fabric;
        if (fabricZone) {
          roughness = fabricZone.roughness ?? roughness;
        }
        return new THREE.MeshStandardMaterial({
          color: 0xffffff, // Use white to avoid dark base color
          map: null, // Explicitly clear any failed texture maps
          roughness,
          metalness: 0.0,
          name: materialName + "_fabric",
        });
      }

      case "wood": {
        // Get properties from MaterialManager
        let roughness = 0.7;
        let metalness = 0.0;
        if (this.app.getMaterialManager()) {
          const legsZoneProps = this.app.getMaterialManager().materialZoneProperties?.legs;
          if (legsZoneProps) {
            roughness = legsZoneProps.roughness ?? 0.7;
            metalness = legsZoneProps.metalness ?? 0.0;
          }
        }

        // Load leg textures from furniture_legs folder
        const legMaterial = new THREE.MeshStandardMaterial({
          color: 0xffffff, // Use white for proper texture color
          roughness: roughness,
          metalness: metalness,
          side: THREE.FrontSide,
          name: materialName + "_wood",
        });

        // Load leg textures based on current selection
        this.loadLegTextures(legMaterial, this.currentLegTextureType);
        return legMaterial;
      }

      case "label": {
        // Get properties from MaterialManager
        let roughness = 0.5;
        let metalness = 0.0;
        if (this.app.getMaterialManager()) {
          const accentsZoneProps = this.app.getMaterialManager().materialZoneProperties?.accents;
          if (accentsZoneProps) {
            roughness = accentsZoneProps.roughness ?? 0.5;
            metalness = accentsZoneProps.metalness ?? 0.0;
          }
        }

        // Load label texture from furniture_label folder
        const labelMaterial = new THREE.MeshStandardMaterial({
          color: 0xffffff, // Use white for proper texture color
          roughness: roughness,
          metalness: metalness,
          side: THREE.DoubleSide, // Visible on both sides
          name: materialName + "_label",
        });

        this.loadLabelTexture(labelMaterial, mesh?.geometry);
        return labelMaterial;
      }

      case "metal": {
        // Get properties from MaterialManager
        let roughness = 0.3;
        let metalness = 0.8;
        if (this.app.getMaterialManager()) {
          const metalZoneProps = this.app.getMaterialManager().materialZoneProperties?.metal;
          if (metalZoneProps) {
            roughness = metalZoneProps.roughness ?? 0.3;
            metalness = metalZoneProps.metalness ?? 0.8;
          }
        }

        return new THREE.MeshStandardMaterial({
          color: 0x404040, // Dark metallic
          roughness: roughness,
          metalness: metalness,
          side: THREE.FrontSide,
          name: materialName + "_metal",
        });
      }

      case "plastic": {
        // Get properties from MaterialManager
        let roughness = 0.4;
        let metalness = 0.1;
        if (this.app.getMaterialManager()) {
          const plasticZoneProps = this.app.getMaterialManager().materialZoneProperties?.plastic;
          if (plasticZoneProps) {
            roughness = plasticZoneProps.roughness ?? 0.4;
            metalness = plasticZoneProps.metalness ?? 0.1;
          }
        }

        return new THREE.MeshStandardMaterial({
          color: 0x2a2a2a, // Dark plastic
          roughness: roughness,
          metalness: metalness,
          side: THREE.FrontSide,
          name: materialName + "_plastic",
        });
      }

      default:
        return new THREE.MeshStandardMaterial({
          color: 0xaaaaaa, // Neutral gray
          roughness: 0.6,
          metalness: 0.1,
          side: THREE.FrontSide,
          name: materialName + "_default",
        });
    }
  }

  loadLegTextures(material, type = "dark") {
    if (type === "black") {
      material.color.set(0x111111);
      material.map = null;
      material.normalMap = null;
      material.needsUpdate = true;
      return;
    }

    const texturePath = `textures/furniture_legs/${type}-legs-texture.jpg`;
    const normalPath = `textures/furniture_legs/${type}-legs-normal-map.jpg`;
    const cacheKey = `legs_${type}`;

    // Get repeat values from MaterialManager's zone properties
    let repeatX = 1;
    let repeatY = 1;
    if (this.app.getMaterialManager()) {
      const legsZoneProps = this.app.getMaterialManager().materialZoneProperties?.legs;
      if (legsZoneProps?.repeat) {
        repeatX = legsZoneProps.repeat[0];
        repeatY = legsZoneProps.repeat[1];
      }
    }

    // Check cache first
    if (this.furnitureTextureCache[cacheKey]) {
      const cached = this.furnitureTextureCache[cacheKey];
      material.map = cached.texture;
      material.map.repeat.set(repeatX, repeatY);
      material.normalMap = cached.normalMap;
      material.normalMap.repeat.set(repeatX, repeatY);
      material.needsUpdate = true;
      return;
    }

    const texture = this._loadTiledTexture(texturePath, repeatX, repeatY, { srgb: true });
    material.map = texture;
    const normalMap = this._loadTiledTexture(normalPath, repeatX, repeatY);
    material.normalMap = normalMap;

    // Cache the textures
    this.furnitureTextureCache[cacheKey] = { texture, normalMap };

    material.needsUpdate = true;
  }

  _loadTiledTexture(path, repeatX, repeatY, { srgb = false, anisotropic = true } = {}) {
    const texture = this.textureLoader.load(path);
    if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeatX, repeatY);
    if (anisotropic && this.renderer) texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    return texture;
  }

  loadShelfWoodTexture(material) {
    const texturePath = `textures/zip_shelf/oak_1k.jpg`;
    const normalPath = `textures/zip_shelf/oak_nor_1k.png`;
    const cacheKey = 'shelf_wood';

    material.color.set(0xffffff);
    material.roughness = 0.7;
    material.metalness = 0.0;

    if (this.furnitureTextureCache[cacheKey]) {
      const cached = this.furnitureTextureCache[cacheKey];
      material.map = cached.texture;
      material.normalMap = cached.normalMap;
      material.needsUpdate = true;
      return;
    }

    const texture = this._loadTiledTexture(texturePath, 2, 2, { srgb: true });
    const normalMap = this._loadTiledTexture(normalPath, 2, 2, { anisotropic: false });

    material.map = texture;
    material.normalMap = normalMap;
    this.furnitureTextureCache[cacheKey] = { texture, normalMap };
    material.needsUpdate = true;
  }

  // True when `child`'s `_wood` material belongs to a dedicated wood surface
  // (an item declaring a "wood" material zone, with a wood-named mesh node)
  // rather than a leg. Keeps oiko-sta's tabletop on the wood-finish path while
  // legs and clooods/oiko-ts wood stands stay on leg textures.
  isWoodSurface(furnitureData, child) {
    const zones = furnitureData?.properties?.materialZones;
    if (!Array.isArray(zones) || !zones.includes("wood")) return false;
    const name = child?.name ? child.name.toLowerCase() : "";
    return name.includes("wood") && !name.includes("leg");
  }

  // Whether a furniture element has at least one dedicated wood surface
  // (drives the per-element Wood Finish picker in the selection UI).
  isWoodSurfaceElement(furnitureElement) {
    const furnitureData = furnitureElement?.furnitureData;
    if (!furnitureElement?.mesh) return false;
    if (!furnitureData?.properties?.materialZones?.includes("wood")) return false;
    let found = false;
    furnitureElement.mesh.traverse((child) => {
      if (found) return;
      if (child.isMesh && child.material && this.isWoodSurface(furnitureData, child)) {
        found = true;
      }
    });
    return found;
  }

  // Set a single element's wood finish ("light" | "dark") and retexture only its
  // wood surfaces. Per-element — one oiko-sta can be dark while another is light.
  setElementWoodType(furnitureElement, woodType) {
    if (!furnitureElement?.mesh) return;
    furnitureElement.woodType = woodType;
    const furnitureData = furnitureElement.furnitureData;
    furnitureElement.mesh.traverse((child) => {
      if (child.isMesh && child.material) {
        const materialName = child.material.name ? child.material.name.toLowerCase() : "";
        if (materialName.includes("_wood") && this.isWoodSurface(furnitureData, child)) {
          this.loadWoodTexture(child.material, woodType);
        }
      }
    });
  }

  // Scene-wide wood finish ("light" | "dark") for every element with a dedicated wood
  // surface, and the default for anything placed afterwards. Drives the `?boardColor=`
  // override / embed `setOptions` message; the per-element picker still wins locally.
  // @returns {number} how many elements were retextured
  setAllWoodTypes(woodType) {
    this.currentWoodType = woodType;
    let applied = 0;
    this.furniture.forEach((furnitureElement) => {
      if (!this.isWoodSurfaceElement(furnitureElement)) return;
      this.setElementWoodType(furnitureElement, woodType);
      applied++;
    });
    return applied;
  }

  // Dedicated wood-surface texture (oiko-sta tabletop). Each finish has its own authored oak
  // scan, so no tint; the normal map is shared and both are rotated 90° to run the grain
  // along the table's longer side.
  loadWoodTexture(material, type) {
    const woodType = normalizeWoodType(type);

    // Tag the material so leg-texture passes never overwrite it. The tag lives on
    // the material itself, so it's correct even when a parent's mesh.traverse walks
    // into an ATTACHED wood-surface child (e.g. an oiko-sta seated on an oiko-v) —
    // where the traversing element's furnitureData would misclassify it.
    if (!material.userData) material.userData = {};
    material.userData.woodSurface = true;

    material.color.set(0xffffff);
    material.roughness = 0.7;
    material.metalness = 0.0;

    // Rotate 90° about the texture centre so the grain runs lengthwise.
    const load = (path, srgb) => {
      const tex = this.textureLoader.load(path);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.center.set(0.5, 0.5);
      tex.rotation = Math.PI / 2;
      if (srgb) {
        tex.colorSpace = THREE.SRGBColorSpace;
        if (this.renderer) tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      }
      return tex;
    };

    const cache = this.furnitureTextureCache;
    // One entry per finish; the normal map is shared, so it loads once for both.
    material.map = cache[`wood_surface_${woodType}`] ??= load(`textures/wood/oak-texture-${woodType}.jpg`, true);
    material.normalMap = cache.wood_surface_normal ??= load(`textures/wood/oak-texture-map.png`, false);
    material.needsUpdate = true;
  }

  // Bounding box of a geometry's UV (TEXCOORD_0) attribute.
  _getUVBounds(geometry) {
    const uv = geometry?.attributes?.uv;
    if (!uv) return null;
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    for (let i = 0; i < uv.count; i++) {
      const u = uv.getX(i);
      const v = uv.getY(i);
      if (u < uMin) uMin = u;
      if (u > uMax) uMax = u;
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
    return { uMin, uMax, uRange: uMax - uMin, vMin, vMax, vRange: vMax - vMin };
  }

  // Sign of a UV island's summed signed area, in index order: +1 is wound the
  // same way round as the surface it prints on (gltf front faces are CCW).
  _getUVWinding(geometry) {
    const uv = geometry?.attributes?.uv;
    if (!uv) return 0;
    const index = geometry.index;
    const count = index ? index.count : uv.count;
    const vertex = (i) => (index ? index.getX(i) : i);
    let sum = 0;
    for (let t = 0; t + 2 < count; t += 3) {
      const i0 = vertex(t), i1 = vertex(t + 1), i2 = vertex(t + 2);
      const u0 = uv.getX(i0), v0 = uv.getY(i0);
      sum += (uv.getX(i1) - u0) * (uv.getY(i2) - v0)
           - (uv.getY(i1) - v0) * (uv.getX(i2) - u0);
    }
    return Math.sign(sum);
  }

  loadLabelTexture(material, geometry) {
    const texturePath = `textures/furniture_label/feydom-label.jpg`;
    const baseKey = "feydom_label_base";

    // The feydom label is a single logo image, NOT a tiled accent fabric, so it
    // must not inherit the accents zone repeat. Collections unwrap the tag mesh
    // differently, so the correct texture transform is derived per-mesh from its
    // authored UV bounds rather than forced globally.
    const applyTransform = (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.center.set(0.5, 0.5);
      if (this.renderer) {
        texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      }

      // The tag is a thin folded loop, unwrapped one of two ways: faces laid
      // end-to-end along the LENGTH (diagon/clooods/bard-e2.., that axis spanning
      // ~2 logos) or stacked across the WIDTH (cascais: length=V; bard-e1/q6/st-2/
      // td-3/tuck: length=U). Both are told apart by axis ratio and fitted to the
      // authored island, never to uv [0,1] — an inset island would otherwise
      // magnify the logo by 1/range (q6 printed 12% large), and st-2/td-3/tuck
      // carry bard-e1's layout at half the UV scale.
      const b = this._getUVBounds(geometry);
      // Handedness is the other half of the layout: the fit below is uniform per
      // mesh, so only a mirror can answer an island wound against the surface.
      const winding = this._getUVWinding(geometry);
      const unwrapped = b && b.uRange > 1e-6 && b.vRange > 1e-6;
      const facesAlongU = unwrapped && b.uRange > 3 * b.vRange;
      const lengthIsV = unwrapped && !facesAlongU && b.vRange > b.uRange;
      // One logo fitted across `faces` equal bands of an axis.
      const fit = (min, range, faces) => {
        const repeat = faces / range;
        return { repeat, offset: repeat * (0.5 - (min + range / (2 * faces))) };
      };
      if (facesAlongU) {
        // One logo per end-to-end face; the shared width band is shown natively.
        const len = fit(b.uMin, b.uRange, 2);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.rotation = 0;
        texture.repeat.set(len.repeat, 1);
        texture.offset.set(len.offset, 0);
      } else if (unwrapped) {
        const [lenMin, lenRange, faceMin, faceRange] = lengthIsV
          ? [b.vMin, b.vRange, b.uMin, b.uRange]
          : [b.uMin, b.uRange, b.vMin, b.vRange];
        const len = fit(lenMin, lenRange, 1);
        const face = fit(faceMin, faceRange, 2);
        // The -90° rotation flips the loop face's winding on both axes, so
        // length=V mirrors the fit to keep glyphs reading forwards.
        const parity = lengthIsV ? -1 : 1;
        // Length clamps so the logo shows once; the face axis repeats it onto both.
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.rotation = lengthIsV ? -Math.PI / 2 : 0;
        texture.repeat.set(len.repeat, parity * face.repeat);
        texture.offset.set(parity * len.offset, parity * face.offset);
      } else {
        // Unknown layout: use the texture as authored, clamped so stray UVs show
        // the label edge instead of tiling extra logos.
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.rotation = 0;
        texture.repeat.set(1, 1);
        texture.offset.set(0, 0);
      }

      // Glyphs read forwards when the matrix's handedness matches the island's,
      // so mirror the length axis when they disagree — tuck/td-3/st-2/clooods-c
      // ship reversed islands, which print mirrored on *both* faces of the loop.
      // Its offset is odd in repeat.x, so negating both keeps the same band.
      if (winding && winding !== Math.sign(texture.repeat.x * texture.repeat.y)) {
        texture.repeat.x *= -1;
        texture.offset.x *= -1;
      }

      texture.needsUpdate = true;
      material.map = texture;
      material.needsUpdate = true;
    };

    // Cache the loaded image once; clone per-mesh so each tag keeps its own
    // transform (offset/repeat differ between meshes in the same scene).
    const base = this.furnitureTextureCache[baseKey];
    if (base) {
      applyTransform(base.clone());
      return;
    }

    const texture = this.textureLoader.load(texturePath);
    this.furnitureTextureCache[baseKey] = texture;
    applyTransform(texture);
  }

  determineGLTFMaterialType(materialName, meshName) {
    const matName = (materialName || "").toLowerCase();
    const meshNameLower = (meshName || "").toLowerCase();

    // Check for explicit fabric materials FIRST
    if (
      matName === "fabric" ||
      matName.includes("fabric") ||
      matName.includes("upholstery") ||
      matName.includes("seat") ||
      matName.includes("cushion")
    ) {
      return "fabric";
    }

    // Before wood: both branches read the mesh name, so `legs_plastic_mesh` would otherwise
    // classify as a wooden leg. No shipped model carries both keywords in one name.
    if (
      matName.includes("plastic") ||
      matName.includes("rubber") ||
      meshNameLower.includes("plastic") ||
      meshNameLower.includes("rubber")
    ) {
      return "plastic";
    }

    // Check for wood materials (legs, frames) - these should NOT get fabric textures
    if (
      matName === "legs" ||
      matName.includes("wood") ||
      matName.includes("leg") ||
      matName.includes("frame") ||
      meshNameLower.includes("leg") ||
      meshNameLower.includes("wood") ||
      meshNameLower.includes("frame") ||
      meshNameLower.includes("legs")
    ) {
      return "wood";
    }

    // Check for metal materials
    if (
      matName.includes("metal") ||
      matName.includes("steel") ||
      matName.includes("aluminum") ||
      matName.includes("chrome") ||
      meshNameLower.includes("metal") ||
      meshNameLower.includes("hinge") ||
      meshNameLower.includes("hindge") ||
      meshNameLower.includes("screw") ||
      meshNameLower.includes("hardware")
    ) {
      return "metal";
    }


    // Buttons are plastic; the plastic/rubber keywords are already handled above wood.
    if (meshNameLower.includes("button")) {
      return "plastic";
    }

    // Check for label materials
    if (
      matName.includes("label") ||
      matName.includes("logo") ||
      matName.includes("brand") ||
      meshNameLower.includes("label") ||
      meshNameLower.includes("logo") ||
      meshNameLower.includes("brand")
    ) {
      return "label";
    }

    // Default to wood for unknown materials to be safe (avoid applying fabric to everything)
    return "wood";
  }

  // ===== LEG TEXTURE MANAGEMENT =====
  updateAllLegTextures(legType) {
    // Update the current leg texture type
    this.currentLegTextureType = legType;

    // Clear the leg texture cache to force reload with new type
    const cacheKeys = Object.keys(this.furnitureTextureCache).filter(key =>
      key.startsWith('legs_')
    );
    cacheKeys.forEach(key => {
      const cached = this.furnitureTextureCache[key];
      if (cached.texture) cached.texture.dispose();
      if (cached.normalMap) cached.normalMap.dispose();
      delete this.furnitureTextureCache[key];
    });

    // Update all existing furniture
    this.furniture.forEach((furnitureElement) => {
      if (!furnitureElement || !furnitureElement.mesh) return;
      if (furnitureElement.furnitureData?.collection === 'zip') return;
      const furnitureData = furnitureElement.furnitureData;
      // Same gate as the spawn path: legTextures:false keeps black legs.
      const elementLegType = this.isLegTexturesEnabled(furnitureData) ? legType : "black";

      furnitureElement.mesh.traverse((child) => {
        if (child.isMesh && child.material) {
          const materialName = child.material.name ? child.material.name.toLowerCase() : "";

          // Check if this is a wood/leg material — but leave dedicated wood
          // surfaces (oiko-sta tabletop) to the wood-finish path. The
          // `woodSurface` tag (set by loadWoodTexture) is authoritative even when
          // this traverse walks into an attached wood-surface child.
          if (
            materialName.includes("_wood") &&
            !child.material.userData?.woodSurface &&
            !this.isWoodSurface(furnitureData, child)
          ) {
            // Update the leg textures on this material
            this.loadLegTextures(child.material, elementLegType);
          }
        }
      });
    });
  }
}
